import { useState, useEffect } from "react";
import StarsBackground from "@/components/StarsBackground";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ShoppingBag, Minus, Plus, Trash2, RefreshCw, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ErrorBoundaryWithRecovery } from '@/components/common/ErrorBoundaryWithRecovery';
import { toast } from "sonner";
import { validatePaymentRequest, type PaymentRequest, type PaymentResponse, type PaymentError } from "@/types/payment";
import { clientRateLimiter } from "@/utils/clientRateLimiter";
import { trackUserAction } from "@/utils/monitoring-client";
import { ShippingProgress } from "@/components/ShippingProgress";
import { CartAbandonmentTimer } from "@/components/checkout/CartAbandonmentTimer";
import { TrustBadges } from "@/components/checkout/TrustBadges";
import { DiscountCodeInput, type AppliedDiscount } from "@/components/checkout/DiscountCodeInput";
import { analytics } from "@/utils/analytics";
import EmptyCartRecommendations from "@/components/cart/EmptyCartRecommendations";

const CartContent = () => {
  const { items, updateQuantity, removeFromCart, getCartTotal, addToCart } = useCart();
  const { user } = useAuth();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [showSubscribeUpsell, setShowSubscribeUpsell] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount | null>(null);

  // Check if there are non-subscription items
  const hasOneTimeItems = items.some(item => !item.isSubscription);

  // Calculate discounted total
  const getDiscountedTotal = () => {
    const subtotal = getCartTotal();
    if (!appliedDiscount) return subtotal;
    
    if (appliedDiscount.discountType === 'percentage') {
      return subtotal * (1 - appliedDiscount.discountValue / 100);
    }
    return Math.max(0, subtotal - appliedDiscount.discountValue);
  };

  const getDiscountAmount = () => {
    const subtotal = getCartTotal();
    if (!appliedDiscount) return 0;
    
    if (appliedDiscount.discountType === 'percentage') {
      return subtotal * (appliedDiscount.discountValue / 100);
    }
    return Math.min(appliedDiscount.discountValue, subtotal);
  };

  const handleConvertToSubscription = (item: any) => {
    // Remove the one-time item and add as subscription
    removeFromCart(item.product.id);
    addToCart(item.product, item.selectedPackSize, true, item.selectedFlavor);
    toast.success(`Converted to subscription and saved 15%!`);
  };

  // Auto-save cart for logged-in users
  useEffect(() => {
    if (user && items.length > 0) {
      const saveCart = async () => {
        try {
          await supabase.functions.invoke('manage-cart-sync', {
            body: {
              action: 'save',
              cartData: items
            }
          });
        } catch (error) {
          console.error('Failed to save cart:', error);
        }
      };
      
      const debounce = setTimeout(saveCart, 2000);
      return () => clearTimeout(debounce);
    }
  }, [items, user]);

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    // Validate guest email if not logged in
    if (!user && !guestEmail) {
      toast.error("Please provide your email to continue");
      return;
    }

    if (!user && guestEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      toast.error("Please provide a valid email address");
      return;
    }

    // Rate limiting - 5 checkout attempts per minute
    if (!clientRateLimiter.canProceed('checkout', 5, 60000)) {
      return;
    }

    // Track checkout start
    trackUserAction('checkout_started', {
      cart_value: getCartTotal(),
      item_count: items.reduce((count, item) => count + item.quantity, 0),
    });

    setIsCheckingOut(true);
    
    try {
      const customerEmail = user?.email || guestEmail;
      
      // Send only product IDs to server - server will verify prices
      const requestBody: any = {
        items: items.map(item => ({
          productId: item.product.id,
          selectedPackSize: item.selectedPackSize,
          quantity: item.quantity,
          isSubscription: item.isSubscription
        })),
        customerEmail
      };

      // Include discount if applied
      if (appliedDiscount) {
        requestBody.discount = {
          code: appliedDiscount.code,
          discountType: appliedDiscount.discountType,
          discountValue: appliedDiscount.discountValue
        };
      }

      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: requestBody
      });

      if (error) {
        throw new Error(`Payment setup failed: ${error.message || 'Unknown error'}`) as PaymentError;
      }

      if (!data) {
        throw new Error('No response data from payment service') as PaymentError;
      }

      const paymentResponse = data as PaymentResponse;
      if (!paymentResponse.url) {
        throw new Error('Payment service did not return a checkout URL') as PaymentError;
      }
      
      // Redirect to Stripe checkout
      window.location.href = paymentResponse.url;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Checkout failed: ${errorMessage}`);
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <StarsBackground />
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-8">
            <Link to="/" className="flex items-center text-solr-starfield-silver hover:text-solr-nebula-purple transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span>Continue shopping</span>
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold ml-auto">Your Cart</h1>
          </div>
          
          {items.length === 0 ? (
            <div className="cosmic-card p-8">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ShoppingBag className="w-16 h-16 text-solr-starfield-silver/50 mb-4" />
                <h2 className="text-xl font-medium mb-2">Your cart is empty</h2>
                <p className="text-solr-starfield-silver mb-6 max-w-md">
                  Looks like you haven&apos;t added any cosmic neural enhancers to your cart yet.
                </p>
                <Link to="/shop">
                  <Button className="btn-primary">Browse Products</Button>
                </Link>
              </div>
              
              {/* Recommendations for empty cart */}
              <EmptyCartRecommendations />
            </div>
          ) : (
            <>
              <ShippingProgress currentTotal={getCartTotal()} />
            <div className="space-y-6">
              <div className="cosmic-card p-6">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.product.id} className="flex flex-col md:flex-row items-start md:items-center py-4 border-b border-solr-nebula-purple/10 last:border-b-0">
                      <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
                        <Link to={`/shop/${item.product.id}`}>
                          <img 
                            src={item.product.image} 
                            alt={item.product.name} 
                            className="w-24 h-24 object-contain"
                          />
                        </Link>
                      </div>
                      
                      <div className="flex-grow space-y-1 mb-4 md:mb-0">
                        <Link to={`/shop/${item.product.id}`} className="text-lg font-medium hover:text-solr-nebula-purple transition-colors">
                          {item.product.name}
                        </Link>
                        <div className="text-xs text-solr-nebula-purple">{item.product.tagline}</div>
                        <div className="text-sm text-solr-starfield-silver space-y-1">
                          <div className="flex items-center gap-2">
                            {item.isSubscription ? (
                              <span className="flex items-center gap-1 text-solr-aurora">
                                <RefreshCw className="w-3 h-3" /> Subscription
                              </span>
                            ) : (
                              <span>{item.selectedPackSize}-pack (One-time)</span>
                            )}
                          </div>
                          {item.selectedFlavor && <div>Flavor: {item.selectedFlavor}</div>}
                          <div>
                            {(() => {
                              const packSize = item.product.packSizes.find(pack => pack.size === item.selectedPackSize);
                              const price = item.isSubscription ? (packSize?.subscription_price || packSize?.price || 0) : (packSize?.price || 0);
                              return `£${price.toFixed(2)} each`;
                            })()}
                          </div>
                          {/* Subscribe & Save Upsell */}
                          {!item.isSubscription && (
                            <button
                              onClick={() => handleConvertToSubscription(item)}
                              className="flex items-center gap-1 text-xs text-solr-energy hover:text-solr-energy/80 transition-colors mt-1"
                            >
                              <Sparkles className="w-3 h-3" />
                              Subscribe & Save 15%
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-6 w-full md:w-auto">
                        <div className="flex items-center border border-solr-nebula-purple/20 rounded-md">
                          <button 
                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                            className="p-1 hover:bg-white/5 text-solr-starfield-silver"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-10 text-center">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                            className="p-1 hover:bg-white/5 text-solr-starfield-silver"
                            aria-label="Increase quantity"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="text-right font-medium">
                          {(() => {
                            const packSize = item.product.packSizes.find(pack => pack.size === item.selectedPackSize);
                            const price = item.isSubscription ? (packSize?.subscription_price || packSize?.price || 0) : (packSize?.price || 0);
                            return `£${(price * item.quantity).toFixed(2)}`;
                          })()}
                        </div>
                        
                        <button 
                          onClick={() => removeFromCart(item.product.id)}
                          className="text-solr-starfield-silver hover:text-red-500 transition-colors"
                          aria-label="Remove item"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {!user && (
                <div className="cosmic-card p-6 mb-6">
                  <h3 className="text-lg font-medium mb-4">Guest Checkout</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="guest-name">Name</Label>
                      <Input
                        id="guest-name"
                        type="text"
                        placeholder="Enter your name"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        className="bg-solr-space-dark/50 border-solr-nebula-purple/20"
                      />
                    </div>
                    <div>
                      <Label htmlFor="guest-email">Email *</Label>
                      <Input
                        id="guest-email"
                        type="email"
                        placeholder="Enter your email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        required
                        className="bg-solr-space-dark/50 border-solr-nebula-purple/20"
                      />
                      <p className="text-xs text-solr-starfield-silver mt-1">
                        We'll send your order confirmation here
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Discount Code Section */}
              <div className="cosmic-card p-6">
                <h3 className="text-lg font-medium mb-4">Discount Code</h3>
                <DiscountCodeInput
                  cartTotal={getCartTotal()}
                  onApply={setAppliedDiscount}
                  appliedDiscount={appliedDiscount}
                />
              </div>

              <div className="cosmic-card p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div className="mb-4 md:mb-0">
                    <h3 className="text-lg font-medium mb-1">Order Summary</h3>
                    <p className="text-sm text-solr-starfield-silver">
                      Shipping and taxes will be calculated at checkout
                    </p>
                  </div>
                  
                  <div className="space-y-3 w-full md:w-auto md:min-w-[250px]">
                    <div className="flex justify-between items-center text-solr-starfield-silver">
                      <span>Subtotal:</span>
                      <span>£{getCartTotal().toFixed(2)}</span>
                    </div>
                    
                    {appliedDiscount && (
                      <div className="flex justify-between items-center text-solr-aurora">
                        <span>Discount ({appliedDiscount.code}):</span>
                        <span>-£{getDiscountAmount().toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="border-t border-solr-nebula-purple/20 pt-3">
                      <div className="flex justify-between items-center font-medium">
                        <span>Total:</span>
                        <span className="text-xl">£{getDiscountedTotal().toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleCheckout}
                      disabled={isCheckingOut || items.length === 0}
                      className="btn-primary w-full md:w-auto min-w-[200px] relative"
                    >
                      {isCheckingOut ? (
                        <div className="flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Processing...
                        </div>
                      ) : (
                        "Checkout"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}
          
          {/* Trust Badges */}
          {items.length > 0 && (
            <div className="mt-8">
              <TrustBadges />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const Cart = () => (
  <ErrorBoundaryWithRecovery>
    <CartContent />
  </ErrorBoundaryWithRecovery>
);

export default Cart;
